import { Box } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as appReportService from "source/models/services/appReportService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { TreeCheckBoxDropdownItems } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown/TreeCheckboxDropdownItems";
import { ITreeListItem, TreeList } from "source/components/framework/List/TreeList";
import { useEffect, useState } from "react";
import { AppReportEditItem, IAppReportEditItem } from "ui/Scripts/source/models/appReport/appReportEditItem";
import { Base } from "ui/Scripts/source/framework/base";
import { StoreUtils } from "source/models/store/storeUtils";
import { IAppReportTemplateEditItem } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateEditItem";

/* eslint-disable no-unused-vars */
const enum AppReportNewDialogPage {
    Unit = "1",
    AppReportTemplate = "2",
}
/* eslint-enable no-unused-vars */

interface IAppReportNewDialogProp {
    companyId: string;
    onOk: (item: IAppReportEditItem, units: IIdTitleNode[]) => void;
    onCancel: () => void;
}

interface IAppReportNewDialogState {
    initialized: boolean;
    page: AppReportNewDialogPage;
    firstPage: AppReportNewDialogPage;
    unitId: string;
    appReportTemplateId: string;
    units: IIdTitleNode[];
    allAllowedUnits: IIdTitleNode[];
    appReportTemplates: IAppReportTemplateEditItem[];
    appReportTemplateTreeListItems: ITreeListItem[];
}

export const AppReportNewDialog = (props: IAppReportNewDialogProp) => {
    const [state, setState] = useState<IAppReportNewDialogState>({
        initialized: false,
        page: AppReportNewDialogPage.Unit,
        firstPage: AppReportNewDialogPage.Unit,
        unitId: "",
        appReportTemplateId: "",
        units: [],
        allAllowedUnits: [],
        appReportTemplates: [],
        appReportTemplateTreeListItems: []
    });

    const getAppReportTemplateTreeListItems = (appReportTemplates: IAppReportTemplateEditItem[]): ITreeListItem[] => {
        return appReportTemplates.map(i => {
            return {
                id: i.id,
                title: i.code,
                divider: false,
                children: []
            };
        });
    };
    
    const updateAfterAppReportTemplateChange = async (appReportTemplateId: string) => {
        const result = await AppUtils.callService(() => appReportService.getAppReportNewAppReportTemplateChange(props.companyId, appReportTemplateId), false, true);
        if (!result) return;
        const oldUnitId = state.unitId;
        let unitId = state.unitId;
        if (result.appReportTemplateUnitIds.length > 0) {
            unitId = result.appReportTemplateUnitIds.find(i => i === state.unitId) || result.appReportTemplateUnitIds[0];
        }
        if (oldUnitId === unitId) return;
        handleChangeUnit(unitId);
    };

    const handleChangeAppReportTemplate = (appReportTemplateId: string) => {
        setState((old) => ({
            ...old,
            appReportTemplateId
        }));
        updateAfterAppReportTemplateChange(appReportTemplateId);
    };

    const updateAfterUnitChange = async (unitId: string) => {
        if (!unitId) return;
        const result = await AppUtils.callService(() => appReportService.getAppReportNewUnitChange(props.companyId, unitId), false, true);
        if (!result || !result.appReportTemplates || result.appReportTemplates.length === 0) return;
        setState((old) => ({
            ...old,
            appReportTemplates: result.appReportTemplates,
            appReportTemplateTreeListItems: getAppReportTemplateTreeListItems(result.appReportTemplates)
        }));
        const appReportTemplateId = result.appReportTemplates.find(i => i.id === state.appReportTemplateId)?.id ?? result.appReportTemplates[0].id;
        if (state.appReportTemplateId === appReportTemplateId) return;
        setState((old) => ({
            ...old,
            appReportTemplateId
        }));
        // handleChangeAppReportTemplate(appReportTemplateId);
    };

    const handleChangeUnit = (unitId: string) => {
        setState((old) => ({
            ...old,
            unitId,
        }));
        updateAfterUnitChange(unitId);
    };

    const getAppReportEditItem = (unitId: string, appReportTemplate: IAppReportTemplateEditItem): IAppReportEditItem => {
        const result = new AppReportEditItem();
        result.id = Base.emptyGuid;
        result.unitId = unitId;
        result.code = appReportTemplate.code;
        result.dataSchema = appReportTemplate.dataSchema;
        result.createUser = StoreUtils.getNowModifyUser();
        result.modifyUser = StoreUtils.getNowModifyUser();
        return result;
    };

    const loadAppReportNew = async () => {
        const result = await AppUtils.callService(() => appReportService.getAppReportNew(props.companyId), false);
        if (!result) {
            props.onCancel();
            return;
        }
        const hasMultipleActiveUnits = IdTitleNode.getActiveCount(result.units) > 1;
        //const hasMultipleAppReportTemplates = result.appReportTemplates.length > 1;
        const firstPage = hasMultipleActiveUnits
            ? AppReportNewDialogPage.Unit
            : AppReportNewDialogPage.AppReportTemplate;
        //ALWAYS SHOW AppReportTemplate PAGE EVENT THERE IS ONLY ONE REPORT TEMPLATE
        // if (!hasMultipleActiveUnits && !hasMultipleAppReportTemplates) {
        //     props.onOk(getAppReportEditItem(result.unitId, result.appReportTemplates[0]), result.allAllowedUnits);
        // }
        setState((old) => ({
            ...old,
            initialized: true,
            firstPage,
            page: firstPage,
            unitId: result.unitId,
            appReportTemplateId: result.appReportTemplateId,
            units: result.units,
            allAllowedUnits: result.allAllowedUnits,
            appReportTemplates: result.appReportTemplates,
            appReportTemplateTreeListItems: getAppReportTemplateTreeListItems(result.appReportTemplates),
        }));
    };

    useEffect(() => {
        loadAppReportNew();
    }, []);

    const getPreviousPageFromAppReportTemplate = (): AppReportNewDialogPage => {
        const hasMultipleActiveUnits = IdTitleNode.getActiveCount(state.units) > 1;
        if (hasMultipleActiveUnits) {
            return AppReportNewDialogPage.Unit;
        }
        return null;
    };

    const handleMoveToPreviousPage = () => {
        let newPage = state.page;
        if (state.page === AppReportNewDialogPage.AppReportTemplate) {
            newPage = getPreviousPageFromAppReportTemplate();
        }
        setState((old) => ({
            ...old,
            page: newPage ?? state.page
        }));
    };
    
    const getNextPageFromUnit = (): AppReportNewDialogPage => {
        // const hasMultipleAppReportTemplates = state.appReportTemplates.length > 1;
        // if (hasMultipleAppReportTemplates) {
        //     return AppReportNewDialogPage.AppReportTemplate;
        // }
        // return null;
        //ALWAYS SHOW AppReportTemplate PAGE EVENT THERE IS ONLY ONE REPORT TEMPLATE
        return AppReportNewDialogPage.AppReportTemplate;
    };

    const handleMoveToNextPage = () => {
        let newPage = state.page;
        if (state.page === AppReportNewDialogPage.Unit) {
            newPage = getNextPageFromUnit();
        }
        setState((old) => ({
            ...old,
            page: newPage ?? state.page
        }));
    };

    const handleOk = async () => {
        const appReportTemplate = state.appReportTemplates.find(i => i.id === state.appReportTemplateId);
        props.onOk(getAppReportEditItem(state.unitId, appReportTemplate), state.allAllowedUnits);
    };

    if (!state.initialized) return null;

    return (
        <Dialog
            id="appReportNewDialog"
            maxWidth="sm"
            maxHeight="600px"
            title={Translations.AddReport + " - " +
                (state.page === AppReportNewDialogPage.Unit
                    ? Translations.SelectUnit
                    : Translations.SelectReportTemplate
                )
            }
            body={
                <Box display="flex" flexDirection="column" height="100%">
                    {state.page === AppReportNewDialogPage.Unit &&
                        <FormField title={Translations.Unit} fullWidth margin="0">
                            <Box sx={{ height: "100%", overflowY: "auto" }} >
                                <TreeCheckBoxDropdownItems
                                    selectedIds={[state.unitId]}
                                    singleSelect={true}
                                    showComment={true}
                                    values={state.units}
                                    onChange={(unitIds: string[]) => handleChangeUnit(unitIds[0])}
                                />
                            </Box>
                        </FormField>
                    }
                    {state.page === AppReportNewDialogPage.AppReportTemplate &&
                        <FormField title={Translations.ReportTemplate} margin="0">
                            <Box sx={{ height: "100%", overflowY: "auto" }} >
                                <TreeList
                                    height="100%"
                                    items={state.appReportTemplateTreeListItems}
                                    selectedId={state.appReportTemplateId}
                                    onSelect={item => handleChangeAppReportTemplate(item.id)}
                                />
                            </Box>
                        </FormField>
                    }
                </Box>
            }
            buttons={[
                { title: Translations.Cancel, variant: "outlined", onClick: props.onCancel },
                {
                    title: Translations.Previous,
                    variant: "outlined",
                    onClick: state.page === state.firstPage
                        ? null
                        : () => handleMoveToPreviousPage()
                },
                {
                    title: Translations.Next,
                    variant: "confirmation",
                    onClick: state.page === AppReportNewDialogPage.Unit //ALWAYS SHOW AppReportTemplate PAGE EVENT THERE IS ONLY ONE REPORT TEMPLATE && state.appReportTemplates.length > 1
                        ? () => handleMoveToNextPage()
                        : handleOk
                },
            ]}
            onClose={props.onCancel}
        />
    );
};